import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Calculator, Lock, LockOpen } from 'lucide-react';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { projectsApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { LockBody, Project, Snapshot, SplitSummary } from '../types/api';
import DatasetSplitPicker from './DatasetSplitPicker';
import ClassCoveragePreview from './ClassCoveragePreview';

const RELEASE_LABEL_MAX = 32;
const DEFAULT_VERSION = 1;
const DEFAULT_RELEASE_LABEL = '0.0';

/**
 * G.6 — Lock / Unlock modal.
 *
 *   Lock mode adds the DatasetSplitPicker (ratio + strategy + seed) +
 *   a live ClassCoveragePreview that re-fetches /stratify-preview on a
 *   300ms debounce as the customer adjusts inputs. After a successful
 *   lock, the modal switches into "confirmation" mode showing exactly
 *   what landed (no second roundtrip — the lock response carries the
 *   split_summary).
 *
 *   Unlock mode is unchanged from pre-G.6: simple confirm + destructive
 *   warning. No split picker (the split goes away on unlock).
 *
 * Locking semantics: backend always returns 200; hard warnings in
 * split_summary.warnings are advisory. Customer can lock even when the
 * picker has flagged a class with <3 instances — the table shows
 * red rows so they know what they're committing to.
 */
interface LockAndSnapshotModalProps {
    open: boolean;
    isDarkMode?: boolean;
    project: Project | null;
    imageCount: number;
    classCount: number;
    /** Count of images with status='pending' (no boxes drawn yet). */
    pendingImageCount?: number;
    orgId?: string;
    onClose: () => void;
    /** Caller updates its project state with the returned row. Snapshot
     *  + splitSummary are only present on lock; on unlock they're undefined. */
    onResult: (project: Project, snapshot?: Snapshot, splitSummary?: SplitSummary) => void;
}

const DEFAULT_SPLIT: NonNullable<LockBody['split']> = {
    ratio: { train: 80, val: 15, test: 5 },
    strategy: 'stratified',
    seed: 42,
};

export default function LockAndSnapshotModal({
    open,
    isDarkMode = false,
    project,
    imageCount,
    classCount,
    pendingImageCount = 0,
    orgId,
    onClose,
    onResult,
}: LockAndSnapshotModalProps) {
    const { t: tCommon } = useTranslation('common');
    const { t: tCanvas } = useTranslation('canvas');
    const { t: tErrors } = useTranslation('errors');

    const [submitting, setSubmitting] = useState(false);

    // Pin lock-vs-unlock mode at open time so a successful action's
    // setProject doesn't cause the modal to flash the opposite mode
    // for one frame before onClose dismisses.
    const [pinnedIsLocked, setPinnedIsLocked] = useState<boolean | null>(null);
    useEffect(() => {
        if (open) {
            setPinnedIsLocked(project?.is_locked ?? false);
            // Reset state on every open so a previous lock's confirmation
            // doesn't bleed across to a new lock/unlock invocation.
            setSplit(DEFAULT_SPLIT);
            setPreview(null);
            setPreviewLoading(false);
            setPreviewError(null);
            setPostLockResult(null);
            setLastFetchedKey(null);
            // IR-3.MM.9 — reset version inputs to defaults on every open.
            // Customer picks fresh values per lock; we don't carry stale
            // inputs across modal opens.
            setVersion(DEFAULT_VERSION);
            setVersionRaw(String(DEFAULT_VERSION));
            setReleaseLabel(DEFAULT_RELEASE_LABEL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // G.6 — controlled split picker state.
    //
    // Manual-trigger model: the algorithm fires when the customer clicks
    // "Calculate split" (or implicitly on modal open + on Lock submit).
    // No debounced auto-fire on every keystroke — keeps the network tab
    // clean and gives the customer full control over when the backend
    // is asked to recompute.
    const [split, setSplit] = useState<NonNullable<LockBody['split']>>(DEFAULT_SPLIT);
    const [preview, setPreview] = useState<SplitSummary | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [lastFetchedKey, setLastFetchedKey] = useState<string | null>(null);

    // IR-3.MM.9 — version (int major) + release_label (free-form
    // minor.patch/tag). Combined for display as
    // "v{version}.{release_label}" (e.g. "v1.0.0", "v2.hotfix-fri").
    // versionRaw holds the input string so we can show in-progress
    // edits like "" without snapping back to 0 on every keystroke.
    const [version, setVersion] = useState<number>(DEFAULT_VERSION);
    const [versionRaw, setVersionRaw] = useState<string>(String(DEFAULT_VERSION));
    const [releaseLabel, setReleaseLabel] = useState<string>(DEFAULT_RELEASE_LABEL);

    const trimmedLabel = releaseLabel.trim();
    // IR-3.MM.9 — accept 0 as a valid major. Customers use "v0.0.1"
    // early-lifecycle (pre-1.0). Negative integers stay invalid.
    const versionValid = Number.isInteger(version) && version >= 0;
    const releaseLabelValid = trimmedLabel.length > 0 && trimmedLabel.length <= RELEASE_LABEL_MAX;
    // Combined "v{N}.{label}" preview the customer sees live as they
    // type. Mirrors the friendly TAR filename the backend will emit
    // (ignode-annotation-<project>-v<N>.<label>.tar) so they know
    // exactly what their download will be called.
    const fullVersionPreview = versionValid && releaseLabelValid
        ? `v${version}.${trimmedLabel}`
        : null;

    // Post-lock confirmation. Once non-null, the modal flips its body
    // to "Locked" + persisted split summary. Footer offers a single
    // Done button. Re-opening the modal resets this.
    const [postLockResult, setPostLockResult] = useState<{
        project: Project;
        snapshot: Snapshot;
        summary: SplitSummary;
    } | null>(null);

    // handleCalculate — fires POST /stratify-preview with the current
    // picker state. Skips identical-config refetches via lastFetchedKey
    // so clicking the button twice without changing anything is free.
    // The button's disabled state reflects this guard.
    const handleCalculate = useCallback(async () => {
        if (!project) return;
        if (imageCount === 0) {
            setPreview(null);
            return;
        }
        const key = JSON.stringify(split);
        if (key === lastFetchedKey) return;
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const data = await projectsApi.stratifyPreview(project.id, split, orgId);
            setPreview(data);
            setLastFetchedKey(key);
        } catch (err) {
            const msg = err instanceof APIClientError && err.code === 'invalid_ratio'
                ? 'Ratio values must each be ≥ 0 and at least one greater than 0.'
                : 'Could not calculate split preview.';
            setPreviewError(msg);
        } finally {
            setPreviewLoading(false);
        }
    }, [project, orgId, split, lastFetchedKey, imageCount]);

    // One initial calculate on modal open so the customer sees the
    // default 80/15/5 preview without having to click the button first.
    // Skips when already locked or in confirmation view.
    useEffect(() => {
        if (!open || !project) return;
        const isLocked = pinnedIsLocked ?? project.is_locked;
        if (isLocked || postLockResult) return;
        if (imageCount === 0) {
            setPreview(null);
            return;
        }
        // Only auto-fetch ONCE when the modal first opens — guarded by
        // lastFetchedKey staying null after reset on open.
        if (lastFetchedKey !== null) return;
        void handleCalculate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, project?.id, pinnedIsLocked, postLockResult, imageCount]);

    // handleSplitChange — wraps setSplit so that ANY picker edit clears
    // the on-screen preview. The table goes empty + the Calculate
    // button stays prominent until the customer asks to recompute.
    // Avoids the customer mistaking a stale result for the current
    // configuration.
    const handleSplitChange = useCallback((next: NonNullable<LockBody['split']>) => {
        setSplit(next);
        setPreview(null);
        setPreviewError(null);
        setLastFetchedKey(null);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!project) return;
        setSubmitting(true);
        try {
            const isLocked = pinnedIsLocked ?? project.is_locked;
            if (isLocked) {
                const res = await projectsApi.unlock(project.id, orgId);
                onResult(res.project, undefined, undefined);
                onClose();
            } else {
                const body: LockBody = {
                    version,
                    release_label: trimmedLabel,
                    split,
                };
                const res = await projectsApi.lock(project.id, orgId, body);
                onResult(res.project, res.snapshot, res.split_summary);
                // Switch to confirmation view inside the same modal —
                // customer reviews then dismisses with Done.
                setPostLockResult({
                    project: res.project,
                    snapshot: res.snapshot,
                    summary: res.split_summary,
                });
            }
        } catch (err) {
            if (err instanceof APIClientError) {
                const code = err.code ?? '';
                if (code === 'version_in_use') {
                    // IR-3.MM.9 — surfaced as a field-level error rather
                    // than a toast so the customer can immediately tweak
                    // the version input and retry.
                    toast.error(`Version ${version} is already used. Pick a different one.`);
                } else if (code === 'already_locked' || code === 'not_locked' || code === 'project_locked') {
                    toast.error(tErrors(code, { defaultValue: tErrors('generic') }));
                } else {
                    toast.error(tErrors('generic'));
                }
            } else {
                toast.error(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }, [project, orgId, pinnedIsLocked, split, version, trimmedLabel, onResult, onClose, tErrors]);

    if (!project) return null;
    const isLocked = pinnedIsLocked ?? project.is_locked;
    const isConfirming = postLockResult !== null;
    // Lock is gated on having a fresh preview that matches the current
    // picker state. Customer MUST click Calculate after every edit so
    // they see exactly what they're committing to. Unlock mode + zero
    // images skip the gate (unlock has no split, no images = nothing
    // to split).
    const lockGatedOnPreview = !isLocked && !isConfirming && imageCount > 0;
    const previewReady = !lockGatedOnPreview || (preview !== null && JSON.stringify(split) === lastFetchedKey);
    // IR-3.MM.9 — version + release_label must both be valid before
    // lock is enabled. Unlock + confirming skip this gate.
    const versionInputsReady = isLocked || isConfirming || (versionValid && releaseLabelValid);
    const lockReady = previewReady && versionInputsReady;

    // Body for each of the three modes.
    const body = (() => {
        if (isLocked) {
            return (
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                    {tCanvas('unlock_modal_body')}
                </p>
            );
        }
        if (isConfirming && postLockResult) {
            return (
                <div className="space-y-4">
                    <div className={cn(
                        'flex items-start gap-2 text-sm rounded-lg border p-3',
                        isDarkMode ? 'bg-emerald-900/20 border-emerald-800 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800',
                    )}>
                        <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                            Project locked. Snapshot{' '}
                            <span className="font-mono">
                                v{postLockResult.snapshot.snapshot_version}
                                {postLockResult.snapshot.release_label
                                    ? `.${postLockResult.snapshot.release_label}`
                                    : ''}
                            </span>{' '}
                            created.
                        </span>
                    </div>
                    <ClassCoveragePreview
                        summary={postLockResult.summary}
                        isDarkMode={isDarkMode}
                        showHeader
                    />
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                    {tCanvas('lock_modal_body')}
                </p>
                <p className={cn(
                    'text-sm rounded-lg border p-3',
                    isDarkMode ? 'bg-primary-900/20 border-primary-800 text-primary-200' : 'bg-primary-50 border-primary-200 text-primary-800',
                )}>
                    {tCanvas('lock_modal_summary', { imageCount, classCount })}
                </p>
                {pendingImageCount > 0 && (
                    <div className={cn(
                        'text-sm rounded-lg border p-3 flex items-start gap-2',
                        isDarkMode ? 'bg-amber-900/20 border-amber-800 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800',
                    )}>
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                            {tCanvas('lock_modal_pending_warning', {
                                defaultValue: 'Warning: {{count}} of {{total}} images are still unannotated. They\'ll be exported as negative samples (no objects). Cancel and finish labeling if that\'s not what you want.',
                                count: pendingImageCount,
                                total: imageCount,
                            })}
                        </span>
                    </div>
                )}
                {/* IR-3.MM.9 — customer-supplied snapshot version. The
                    integer "version" is the major; the free-form
                    "release_label" carries minor/patch/tag info. They
                    combine for display as "v{version}.{release_label}"
                    (e.g. "v1.0.0", "v2.hotfix-fri") — the live preview
                    on the right tells the customer exactly what their
                    snapshot will be named. */}
                <div className={cn(
                    'rounded-lg border p-3 space-y-3',
                    isDarkMode ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50',
                )}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className={cn(
                            'text-sm font-medium',
                            isDarkMode ? 'text-gray-200' : 'text-gray-800',
                        )}>
                            Snapshot version
                        </div>
                        <div className={cn(
                            'text-xs',
                            isDarkMode ? 'text-gray-400' : 'text-gray-500',
                        )}>
                            Pick a version + label for this snapshot.
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-start">
                        <label className="flex flex-col gap-1">
                            <span className={cn(
                                'text-xs font-medium',
                                isDarkMode ? 'text-gray-300' : 'text-gray-700',
                            )}>
                                Version (major)
                            </span>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                value={versionRaw}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    setVersionRaw(raw);
                                    const parsed = parseInt(raw, 10);
                                    // Empty input keeps versionRaw="" but
                                    // version=-1 so the validator flags it
                                    // (0 is now a legitimate major version).
                                    setVersion(Number.isFinite(parsed) ? parsed : -1);
                                }}
                                disabled={submitting}
                                placeholder="0"
                                className={cn(
                                    'rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2',
                                    isDarkMode
                                        ? 'bg-gray-800 border-gray-600 text-gray-100 focus:ring-primary-500'
                                        : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500',
                                    !versionValid && versionRaw !== ''
                                        ? (isDarkMode ? 'border-red-700' : 'border-red-400')
                                        : '',
                                )}
                            />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className={cn(
                                'text-xs font-medium',
                                isDarkMode ? 'text-gray-300' : 'text-gray-700',
                            )}>
                                Release label (minor.patch or tag)
                            </span>
                            <input
                                type="text"
                                maxLength={RELEASE_LABEL_MAX}
                                value={releaseLabel}
                                onChange={(e) => setReleaseLabel(e.target.value)}
                                disabled={submitting}
                                placeholder="0.0"
                                className={cn(
                                    'rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2',
                                    isDarkMode
                                        ? 'bg-gray-800 border-gray-600 text-gray-100 focus:ring-primary-500'
                                        : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500',
                                    !releaseLabelValid && releaseLabel !== ''
                                        ? (isDarkMode ? 'border-red-700' : 'border-red-400')
                                        : '',
                                )}
                            />
                        </label>
                        <div className="flex flex-col gap-1 sm:items-end">
                            <span className={cn(
                                'text-xs font-medium',
                                isDarkMode ? 'text-gray-300' : 'text-gray-700',
                            )}>
                                Full release version
                            </span>
                            <div
                                className={cn(
                                    'rounded-md border px-2 py-1.5 text-sm font-mono whitespace-nowrap',
                                    fullVersionPreview
                                        ? (isDarkMode ? 'bg-emerald-900/20 border-emerald-800 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800')
                                        : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-300 text-gray-400'),
                                )}
                                aria-live="polite"
                            >
                                {fullVersionPreview ?? '—'}
                            </div>
                        </div>
                    </div>
                    <div className={cn(
                        'text-xs',
                        isDarkMode ? 'text-gray-400' : 'text-gray-500',
                    )}>
                        {fullVersionPreview ? (
                            <>Your snapshot will be saved as <span className="font-mono">{fullVersionPreview}</span>.</>
                        ) : (
                            <>Version must be a non-negative integer (0, 1, 2 …); release label is required (up to {RELEASE_LABEL_MAX} characters).</>
                        )}
                    </div>
                </div>
                <DatasetSplitPicker
                    value={split}
                    onChange={handleSplitChange}
                    imageCount={imageCount}
                    isDarkMode={isDarkMode}
                    disabled={submitting}
                />
                {/* Calculate split — customer-triggered run of the
                    algorithm. Disabled when the configuration is
                    unchanged from the last successful fetch (clicking
                    twice would be a no-op) or while a request is
                    in-flight. */}
                <div className={cn(
                    'flex items-center justify-between gap-3 flex-wrap rounded-lg border p-3',
                    !preview && imageCount > 0
                        ? (isDarkMode ? 'border-amber-700/70 bg-amber-900/20' : 'border-amber-300 bg-amber-50')
                        : (isDarkMode ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50'),
                )}>
                    <span className={cn(
                        'text-xs',
                        !preview && imageCount > 0
                            ? (isDarkMode ? 'text-amber-200' : 'text-amber-800')
                            : (isDarkMode ? 'text-gray-400' : 'text-gray-500'),
                    )}>
                        {preview
                            ? 'Adjust the split above and click Calculate to update the preview.'
                            : imageCount === 0
                                ? 'Add images before calculating a split.'
                                : 'Click Calculate to preview the split (required before locking).'}
                    </span>
                    <Button
                        variant="secondary"
                        isDarkMode={isDarkMode}
                        onClick={() => void handleCalculate()}
                        disabled={submitting || previewLoading || imageCount === 0 || JSON.stringify(split) === lastFetchedKey}
                        leftIcon={<Calculator className="h-4 w-4" />}
                    >
                        {previewLoading ? 'Calculating…' : 'Calculate split'}
                    </Button>
                </div>
                {previewError && (
                    <div className={cn(
                        'text-sm rounded-lg border p-3',
                        isDarkMode ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700',
                    )}>
                        {previewError}
                    </div>
                )}
                <ClassCoveragePreview
                    summary={preview}
                    loading={previewLoading}
                    isDarkMode={isDarkMode}
                    showHeader
                />
                {preview && !preview.can_lock && (
                    <div className={cn(
                        'flex items-start gap-2 text-xs rounded-md border p-2',
                        isDarkMode ? 'bg-amber-900/20 border-amber-800 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800',
                    )}>
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                            Your data has issues that may prevent training (highlighted in red above).
                            You can lock anyway and fix the data later — the snapshot will reflect
                            what you have today.
                        </span>
                    </div>
                )}
            </div>
        );
    })();

    const footer = (() => {
        if (isConfirming) {
            return (
                <Button
                    variant="primary"
                    isDarkMode={isDarkMode}
                    onClick={onClose}
                >
                    {tCommon('done', { defaultValue: 'Done' })}
                </Button>
            );
        }
        const lockDisabled = submitting || !lockReady;
        const lockTooltip = !versionInputsReady
            ? 'Set version (non-negative integer) and release label before locking.'
            : !previewReady
                ? 'Click Calculate split above to preview before locking.'
                : undefined;
        const footerHint = !isLocked && !lockReady && imageCount > 0
            ? (!versionInputsReady
                ? 'Set version + release label before locking'
                : 'Calculate the split before locking')
            : null;
        return (
            <>
                {footerHint && (
                    <span
                        className={cn(
                            'mr-auto text-xs inline-flex items-center gap-1',
                            isDarkMode ? 'text-amber-300' : 'text-amber-700',
                        )}
                    >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {footerHint}
                    </span>
                )}
                <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose} disabled={submitting}>
                    {tCommon('cancel')}
                </Button>
                <Button
                    variant={isLocked ? 'destructive' : 'primary'}
                    onClick={() => void handleConfirm()}
                    disabled={lockDisabled}
                    leftIcon={isLocked ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    title={lockTooltip}
                >
                    {submitting
                        ? (isLocked ? tCanvas('unlock_in_progress') : tCanvas('lock_in_progress'))
                        : (isLocked ? tCanvas('unlock_modal_confirm') : tCanvas('lock_modal_confirm'))}
                </Button>
            </>
        );
    })();

    // Wider modal in lock mode (with picker + table) than in unlock or
    // confirmation. Confirmation reuses lock-mode width because it
    // shows the same table.
    const widthClass = isLocked ? 'max-w-md' : 'max-w-3xl';

    // Lock-to-buttons: while submitting OR in confirmation view we
    // disable backdrop dismissal (the customer must explicitly click
    // Done / Cancel / X to close). Pre-lock state retains normal close
    // semantics so customers can cancel cleanly.
    const onCloseGate = submitting || isConfirming ? () => {} : onClose;

    return (
        <Modal
            open={open}
            onClose={onCloseGate}
            title={
                isLocked
                    ? tCanvas('unlock_modal_title')
                    : isConfirming
                        ? 'Project locked'
                        : tCanvas('lock_modal_title')
            }
            widthClass={widthClass}
            isDarkMode={isDarkMode}
            footer={footer}
        >
            {body}
        </Modal>
    );
}
